import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { Flex, Text } from 'theme-ui'
import { Button } from 'ui/Buttons'
import { SignUpForm } from 'components/LoginSignUpForm'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { navigate } from 'gatsby'
import { clearCaptcha } from 'store/session'
import { CAN_I_HAZ_ACCOUNT } from 'store/api/user/user.interface'
import { Challenge } from 'ui/Captcha'
import pick from 'lodash/pick'
import { useSuccessfulRegistrationTracking } from 'utils/campaignTracking'
import useQueryString from 'utils/useQueryString'
import { getPriceInterval } from 'components/Plans/SelectPlan/SelectPlan'
import { setPriceInterval, setSelectedProductPk } from 'store/billing'
import { useGetProductsQuery } from 'store/api/billing/products'
import { CommonPriceInterval, ProductType } from 'store/api/billing/payments.interface'
import useGetUserState from 'store/api/user/useGetUserState'
import { ERROR_ACCOUNT_EXISTS } from 'store/api/errorCodes'
import WhatControlDIsOrIsNot from './WhatControlDIsOrIsNot'
import { clearErrors } from 'store/errors'
import { triggerSignupPixelRequest } from 'store/tracking'
import { TitleSection } from 'components/NewStaticPage'

interface CreateAccountViewProps {
  handleUpdate: (target) => void
  form: { email: string; password: string }
  onPreviousClick: () => void
  // no next click since the transition is handled in the hook usePlanWizardSteps
}
export default function CreateAccountView({
  handleUpdate,
  form,
  onPreviousClick,
}: CreateAccountViewProps): ReactElement | null {
  const dispatch = useAppDispatch()
  const session = useAppSelector(s => s.session)
  const errors = useAppSelector(s => s.errors.errors)
  const [lastGlobalError] = errors
  const isLoggedIn = useAppSelector(s => !!s.persistData.sessionToken)
  const { qs } = useQueryString()

  const shouldForceTrial = useAppSelector(s => s.tracking.shouldForceTrial)
  const { data: productsData } = useGetProductsQuery({})
  const fullControl = productsData?.products?.find(x => x.name === ProductType.FULL)

  const [isRegistrationInProgress, setIsRegistrationInProgress] = useState(false)
  const [captchaChallengeText, setCaptchaChallengeText] = useState('Slide to complete the puzzle')
  const selectedFeaturedProfilePK = useAppSelector(
    s => s.featuredProfiles.selectedFeaturedProfile?.PK,
  )
  const { register, resolveCaptcha } = useGetUserState()

  useSuccessfulRegistrationTracking()
  const shouldShowLoginButton = `${lastGlobalError?.code}` === ERROR_ACCOUNT_EXISTS
  const registrationPayload = useMemo(
    () => ({
      ...form,
      clone_profile_id: selectedFeaturedProfilePK,
      can_i_haz_account: CAN_I_HAZ_ACCOUNT.YES,
    }),
    [form, selectedFeaturedProfilePK],
  )

  useEffect(() => {
    if (session.preAuthResponse?.captcha) {
      setIsRegistrationInProgress(true)
    }
  }, [session.preAuthResponse])

  //cleanup
  useEffect(
    () => () => {
      dispatch(clearErrors())
      dispatch(clearCaptcha())
    },
    [dispatch],
  )

  useEffect(() => {
    if (qs.fullControl && !!fullControl) {
      dispatch(setSelectedProductPk(fullControl.PK))
      dispatch(
        setPriceInterval(
          shouldForceTrial ? CommonPriceInterval.TRIAL : getPriceInterval(fullControl),
        ),
      )
    }
  }, [dispatch, fullControl, qs.fullControl, shouldForceTrial])

  return (
    <>
      <Flex
        sx={{
          width: '100%',
          mb: ['10rem', '6rem'],
          px: ['0.4rem', 0],
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {!isLoggedIn && (
          <>
            <TitleSection error={lastGlobalError?.message}>Get in Control</TitleSection>

            <Flex
              sx={{
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {shouldShowLoginButton ? (
                <Button
                  variant="gradientButton"
                  sx={{
                    minWidth: '24rem',
                  }}
                  data-testid="create-account-login-button"
                  ariaLabel="login button"
                  onClick={(): void => {
                    navigate('/login')
                  }}
                >
                  <Text variant="size17Weight500Line138">Log in</Text>
                </Button>
              ) : (
                <Flex
                  sx={{
                    gap: ['3.2rem', '3.2rem', '8rem'],
                    flexDirection: ['column', 'row'],
                    px: [0, '1.6rem', 0],
                  }}
                >
                  <WhatControlDIsOrIsNot />
                  <Flex
                    sx={{
                      width: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <SignUpForm
                      isRegistrationInProgress={isRegistrationInProgress}
                      handleSubmit={(): void => {}}
                      handleUpdate={handleUpdate}
                      error={''}
                      form={form}
                      isLoading={false}
                      onNextClick={async () => {
                        dispatch(clearErrors())
                        setIsRegistrationInProgress(true)
                        dispatch(triggerSignupPixelRequest())
                        await register(registrationPayload)
                        setIsRegistrationInProgress(false)
                      }}
                      onPreviousClick={onPreviousClick}
                    >
                      {session.preAuthResponse?.captcha && (
                        <Flex sx={{ mx: 'auto' }}>
                          <Challenge
                            captcha={session.preAuthResponse.captcha}
                            completeCaptcha={async (response, trail) => {
                              let result = true
                              // call register with response & trail
                              await resolveCaptcha({
                                ...registrationPayload,
                                solution_answer: response,
                                solution_trail: trail,
                                ...pick(session.preAuthResponse, ['token', 'sig']),
                              }).catch(() => {
                                // if verification failed, re initialize captcha
                                register(registrationPayload)
                                setCaptchaChallengeText('Please try again!')
                                result = false
                              })

                              setIsRegistrationInProgress(!result)
                              return result
                            }}
                            text={captchaChallengeText}
                          />
                        </Flex>
                      )}
                    </SignUpForm>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </>
        )}
      </Flex>
    </>
  )
}
